import { computed, inject } from '@angular/core';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import {
  entityConfig,
  upsertEntities,
  withEntities,
} from '@ngrx/signals/entities';
import { firstValueFrom } from 'rxjs';
import { BehaviorSignalService } from '@thiememeulenhoff/ng-api-gateway';
import { BehaviorSignalsResponse } from '@thiememeulenhoff/types';
import {
  toRequestError,
  withRequestStatus,
} from '../_utils/request-status.feature';

const behaviorSignalEntityConfig = entityConfig({
  entity: {} as BehaviorSignalsResponse,
  // Composite key so different actors for the same content stay separate.
  selectId: (e: BehaviorSignalsResponse) => `${e.actor}|${e.contentIdentifier}`,
});

export const BehaviorSignalStore = signalStore(
  { providedIn: 'root' },
  withEntities(behaviorSignalEntityConfig),
  withRequestStatus(),
  withMethods(
    (store, behaviorSignalService = inject(BehaviorSignalService)) => ({
      behaviorSignalsByContentIdentifier: (
        actor: string,
        contentIdentifier: string,
      ) =>
        computed(() =>
          store
            .entities()
            .find(
              (e) =>
                `${e.actor}|${e.contentIdentifier}` ===
                `${actor}|${contentIdentifier}`,
            ),
        ),
      async fetchBehaviorSignals(
        actor: string,
        contentIdentifier: string,
      ): Promise<BehaviorSignalsResponse> {
        patchState(store, { requestStatus: 'loading' });
        try {
          const response = await firstValueFrom(
            behaviorSignalService.getBehaviorSignals(actor, contentIdentifier),
          );
          patchState(
            store,
            upsertEntities([response], behaviorSignalEntityConfig),
            {
              requestStatus: 'success',
            },
          );
          return response;
        } catch (error) {
          patchState(store, {
            requestStatus: toRequestError(
              error,
              'Failed to fetch behavior signals',
            ),
          });
          throw error;
        }
      },
    }),
  ),
);
